/**
 * What each record affordance requires, named once so every surface asking the same question shares
 * one rule. A rule whose role arm reads a fact only the call site holds — an ownership or assignment
 * check — is built there instead, since no constant can close over it.
 */
import { recordAuthorization } from './accessRules'
import {
  adminsOnly,
  everyRole,
  everyRoleExceptSuppliers,
  noRole,
  nonViewerAdmins,
  nonViewerAdminsUnlessP2pRestricted,
  nonViewOnly,
  superAdmin,
} from './roleAuthorization'
import type { RecordAccessRule } from './accessRules'

export const EDIT_AGREEMENT: RecordAccessRule = {
  authorization: recordAuthorization('agreement', 'canEdit', nonViewerAdmins),
}

/** Renaming, and reparenting from the hierarchy: open to every role before the service answered. */
export const EDIT_AGREEMENT_ANY_ROLE: RecordAccessRule = {
  authorization: recordAuthorization('agreement', 'canEdit', everyRole),
}

export const CANCEL_AGREEMENT: RecordAccessRule = {
  authorization: recordAuthorization('agreement', 'canCancel', nonViewerAdmins),
}

export const DELETE_AGREEMENT: RecordAccessRule = {
  authorization: recordAuthorization('agreement', 'canDelete', nonViewerAdmins),
}

/** Administering an agreement — today, marking it confidential. */
export const ADMINISTER_AGREEMENT: RecordAccessRule = {
  authorization: recordAuthorization('agreement', 'canAdminister', noRole),
}

export const MANAGE_AGREEMENT_DOCUMENTS: RecordAccessRule = {
  authorization: recordAuthorization('agreement', 'canManageDocuments', nonViewOnly),
}

export const EDIT_REQUEST_METADATA: RecordAccessRule = {
  authorization: recordAuthorization('request', 'canEditMetadata', nonViewerAdmins),
}

/**
 * Wider than `EDIT_REQUEST_METADATA` on the same verb by a view-only administrator: the renewals
 * service admits one assigned to the renewal, and no assignment is visible here to narrow it by.
 */
export const EDIT_REQUEST_RENEWAL_DATE: RecordAccessRule = {
  authorization: recordAuthorization('request', 'canEditMetadata', adminsOnly),
}

export const EDIT_REQUEST_PO_NUMBER: RecordAccessRule = {
  authorization: recordAuthorization('request', 'canEditPoNumber', adminsOnly),
}

export const REJECT_REQUEST: RecordAccessRule = {
  authorization: recordAuthorization('request', 'canReject', nonViewerAdmins),
}

export const DELETE_REQUEST: RecordAccessRule = {
  authorization: recordAuthorization('request', 'canDelete', nonViewerAdminsUnlessP2pRestricted),
}

export const REOPEN_REQUEST: RecordAccessRule = {
  authorization: recordAuthorization('request', 'canManageWorkflow', nonViewerAdmins),
}

export const TOGGLE_REQUEST_HIDDEN_STEPS: RecordAccessRule = {
  authorization: recordAuthorization('request', 'canManageWorkflow', adminsOnly),
}

export const MANAGE_REQUEST_DOCUMENTS: RecordAccessRule = {
  authorization: recordAuthorization('request', 'canManageDocuments', nonViewOnly),
}

export const DELETE_REQUEST_DOCUMENT: RecordAccessRule = {
  authorization: recordAuthorization('request', 'canManageDocuments', everyRoleExceptSuppliers),
}

export const MANAGE_RFX: RecordAccessRule = {
  authorization: recordAuthorization('request', 'canManageRfx', everyRole),
}

/** Naming a product that does not exist yet, which creates it on the chosen supplier. */
export const CREATE_SUPPLIER_PRODUCT: RecordAccessRule = {
  authorization: recordAuthorization('supplier', 'canCreateProduct', everyRole),
}

export const VIEW_SUPPLIER_SUBMISSIONS: RecordAccessRule = {
  authorization: recordAuthorization('supplier', 'canViewRelatedSubmissions', everyRole),
}

/** Administering a supplier — its lifecycle settings (archive, active/inactive, merge) and confidentiality. */
export const ADMINISTER_SUPPLIER: RecordAccessRule = {
  authorization: recordAuthorization('supplier', 'canAdminister', superAdmin),
}
